import { Injectable, EventEmitter } from '@angular/core';

import { ClassCoHosts } from '../state/whereby-meeting-coHosts';
import { SocketService } from './socket.service';

@Injectable({
    providedIn: 'root',
})
export class WherebySdkHelperService {
    public userWantsToLeaveProgram: EventEmitter<string> =
        new EventEmitter<string>();

    constructor(private socketService: SocketService) {}

    // --- Host helpers ---
    isHost(
        userId: string,
        facilitatorId: string,
        classCoHosts: ClassCoHosts[],
        isDefaultHost: boolean,
    ): boolean {
        return (
            userId === facilitatorId ||
            classCoHosts.some((coHost) => coHost.userId === userId) ||
            isDefaultHost
        );
    }

    getUserRoleSuffix(
        userId: string,
        facilitatorId: string,
        classCoHosts: ClassCoHosts[],
        isDefaultHost: boolean,
    ): string {
        if (userId === facilitatorId) return ' (Host)';
        if (isDefaultHost && userId !== facilitatorId) return ' (Co-Host)';
        if (classCoHosts.some((coHost) => coHost.userId === userId))
            return ' (Co-Host)';
        return '';
    }

    getUserRole(
        userId: string,
        facilitatorId: string,
        classCoHosts: ClassCoHosts[],
        isDefaultHost: boolean,
    ): string {
        if (userId === facilitatorId) return 'Host';
        if (isDefaultHost && userId !== facilitatorId) return 'Co-Host';
        if (classCoHosts.some((coHost) => coHost.userId === userId))
            return 'Co-Host';
        return 'Participant';
    }

    extractRoomName(roomUrl: string): string {
        try {
            const url = new URL(roomUrl);
            const pathParts = url.pathname.split('/').filter((p) => p);
            return pathParts[pathParts.length - 1] || 'Unknown Room';
        } catch {
            return 'Unknown Room';
        }
    }
    /**
     * Checks if the device is a mobile or tablet.
     * @returns true if screen width is less than 1024px
     */
    getDeviceType() {
        // 1. Check for Mobile/Tablet via Touch Capabilities
        // 'pointer: coarse' means the primary input is a finger (mobile/tablet)
        const isMobileOrTablet = window.matchMedia('(pointer: coarse)').matches;

        // 2. Distinguish Tablet from Mobile using Screen Width
        // Common breakpoint: tablets are typically > 768px in their smallest dimension
        const isLargeScreen = window.matchMedia('(min-width: 769px)').matches;

        if (isMobileOrTablet) {
            return isLargeScreen ? 'tablet' : 'mobile';
        }

        // 3. Specific User Agent Fallback (Still useful for specific OS detection)
        const ua = navigator.userAgent;
        if (/Android|iPhone|iPod|IEMobile|Opera Mini/i.test(ua))
            return 'mobile';
        if (/Tablet|iPad|Playbook|Silk/i.test(ua)) return 'tablet';

        // 4. Default to Desktop
        return 'desktop';
    }

    // --- Logging helpers ---
    logUserJoinedMeeting(
        userId: string,
        username: string,
        facilitatorId: string,
        roomUrl: string,
        roomKey: string,
        isUserHost: boolean,
        isDefaultHost: boolean,
        classCoHosts: ClassCoHosts[],
    ): void {
        this.socketService.logToServer('info', 'User joined meeting', {
            userId: userId,
            username: username,
            role: this.getUserRole(
                userId,
                facilitatorId,
                classCoHosts,
                isDefaultHost,
            ),
            roomUrl: roomUrl,
            roomName: this.extractRoomName(roomUrl),
            roomKeyUsed: !!roomKey,
            roomKey: roomKey ? '[REDACTED]' : null,
            isHost: isUserHost,
            isDefaultHost: isDefaultHost,
            facilitatorId: facilitatorId,
        });
    }

    logRecordingPrivilegeIssue(
        userId: string,
        username: string,
        facilitatorId: string,
        roomUrl: string,
        isUserHost: boolean,
        classCoHosts: ClassCoHosts[],
        isDefaultHost: boolean,
    ): void {
        this.socketService.logToServer(
            'warn',
            'User attempted to start recording without host privileges',
            {
                userId: userId,
                username: username,
                role: this.getUserRole(
                    userId,
                    facilitatorId,
                    classCoHosts,
                    isDefaultHost,
                ),
                roomUrl: roomUrl,
                roomName: this.extractRoomName(roomUrl),
                isHost: isUserHost,
                recordingState: 'undefined',
                message:
                    'You do not have host privileges to start recording, or something went wrong with the recording setup.',
            },
        );
    }
}
